import { ExternalLink, Loader2, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTheatreDetail } from '../../hooks/useTheatreDetail';
import { useModuleLatestOutput } from '../../hooks/useModuleLatestOutput';
import type { ActivationCheck } from '../../types/theatre';
import type { EnvironmentRun } from '../../types/environmentRun';
import type { EnvironmentGeneratedCandidate } from '../../types/environmentCandidate';
import { apiClient } from '../../api/client';
import { AgentInspector } from './AgentInspector';
import { AutoresearchInspector } from './AutoresearchInspector';
import { CalibrateInspector } from './CalibrateInspector';
import { ForgeInspector } from './ForgeInspector';
import { MiroSharkInspector } from './MiroSharkInspector';
import { ParadoxInspector } from './ParadoxInspector';
import { EnvironmentPortLegend } from './EnvironmentPortLegend';
import { NodeAssetShelf } from './NodeAssetShelf';
import { MODULE_METADATA } from './data';
import type { AgentData, AnyModuleData, AutoresearchData, CanvasNodeState, ForgeData, MiroSharkData, ModuleKind, TheatreData } from './types';

interface InspectorProps {
  selectedModuleId: string | null;
  nodes: Record<string, CanvasNodeState>;
  onUpdateNodeData: (nodeId: string, patch: Record<string, unknown>) => void;
  theatreId: string | null;
  /** All runs for the current theatre (for cross-run module output search). */
  theatreRuns?: EnvironmentRun[];
}

/** Wrapper that renders the specialized inspector + asset shelf together. */
function InspectorWithShelf({
  children,
  shelfProps,
}: {
  children: React.ReactNode;
  shelfProps: React.ComponentProps<typeof NodeAssetShelf>;
}) {
  return (
    <div className="flex flex-col gap-2">
      {children}
      <aside className="rounded-xl border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] shadow-[var(--e-shadow-sm)] overflow-hidden">
        <NodeAssetShelf {...shelfProps} />
      </aside>
    </div>
  );
}

/** Inspector — docked right-rail detail surface. Dispatches to module-specific panels. */
export function Inspector({ selectedModuleId, nodes, onUpdateNodeData, theatreId, theatreRuns = [] }: InspectorProps) {
  // Hook must be called unconditionally (React rules of hooks)
  const node = selectedModuleId ? nodes[selectedModuleId] : null;
  const { output: latestOutput, outputs: allModuleOutputs, fromRunId } = useModuleLatestOutput(
    theatreRuns,
    selectedModuleId,
    node?.kind ?? null,
  );

  // Fetch candidates for the selected module's run
  const { data: candidateData } = useQuery({
    queryKey: ['moduleCandidates', fromRunId, selectedModuleId],
    queryFn: async () => {
      if (!fromRunId) return { candidates: [] as EnvironmentGeneratedCandidate[] };
      const { data } = await apiClient.get('/api/v1/environment/candidates', {
        params: { run_id: fromRunId },
      });
      // Filter to this module's candidates
      const all = (data.candidates ?? []) as EnvironmentGeneratedCandidate[];
      return { candidates: all.filter((c: EnvironmentGeneratedCandidate) => c.module_id === selectedModuleId) };
    },
    enabled: !!fromRunId && !!selectedModuleId,
    staleTime: 30_000,
  });
  const moduleCandidates = candidateData?.candidates ?? [];

  if (!selectedModuleId || !node) return null;

  // Shared asset shelf props for all inspector types
  const shelfProps = {
    moduleId: selectedModuleId,
    moduleName: 'name' in (node.data as any) ? (node.data as any).name : node.kind,
    outputs: allModuleOutputs,
    candidates: moduleCandidates,
    exportAvailable: allModuleOutputs.length > 0,
    runId: fromRunId,
    theatreId,
  };

  // ── Agent inspector (has editing) ──
  if (node.kind === 'agent_actor') {
    return (
      <InspectorWithShelf shelfProps={shelfProps}>
        <AgentInspector
          data={node.data as AgentData}
          onUpdateData={(patch) => onUpdateNodeData(selectedModuleId, patch)}
        />
      </InspectorWithShelf>
    );
  }

  // ── Theatre construct inspector (real backend data) ──
  if (node.kind === 'theatre_construct_draft') {
    return (
      <InspectorWithShelf shelfProps={shelfProps}>
        <TheatreModuleInspector
          nodeData={node.data as TheatreData}
          theatreId={theatreId}
        />
      </InspectorWithShelf>
    );
  }

  // ── Forge inspector (output-backed, 051) ──
  if (node.kind === 'proposal_source') {
    return (
      <InspectorWithShelf shelfProps={shelfProps}>
        <ForgeInspector nodeData={node.data as ForgeData} output={latestOutput} />
      </InspectorWithShelf>
    );
  }

  // ── Paradox inspector (output-backed, 051) ──
  if (node.kind === 'paradox_scanner') {
    return (
      <InspectorWithShelf shelfProps={shelfProps}>
        <ParadoxInspector output={latestOutput} allOutputs={allModuleOutputs} />
      </InspectorWithShelf>
    );
  }

  // ── MiroShark inspector (output-backed, 052) ──
  if (node.kind === 'miroshark_simulation') {
    return (
      <InspectorWithShelf shelfProps={shelfProps}>
        <MiroSharkInspector nodeData={node.data as MiroSharkData} output={latestOutput} />
      </InspectorWithShelf>
    );
  }

  // ── Calibrate inspector (output-backed, 051) ──
  if (node.kind === 'calibrate_tool') {
    return (
      <InspectorWithShelf shelfProps={shelfProps}>
        <CalibrateInspector output={latestOutput} />
      </InspectorWithShelf>
    );
  }

  // ── Theatre Calibrator inspector (079) ──
  if (node.kind === 'autoresearch_module') {
    // latestOutput is already scoped to this exact module_id by useModuleLatestOutput()
    return (
      <InspectorWithShelf shelfProps={shelfProps}>
        <AutoresearchInspector
          nodeData={node.data as AutoresearchData}
          outputData={(latestOutput?.output_json as any) ?? null}
        />
      </InspectorWithShelf>
    );
  }

  // ── Generic module metadata inspector ──
  const moduleData = node.data as AnyModuleData;
  const meta = MODULE_METADATA[node.kind as ModuleKind];
  if (!meta) return null;

  return (
    <aside className="animate-slide-in-right flex w-full min-h-0 flex-col rounded-xl border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] shadow-[var(--e-shadow-sm)] overflow-hidden">
      <div className="flex items-center gap-2 border-b border-[var(--e-border-secondary)] px-4 py-3 shrink-0">
        <span className="text-[12px] font-semibold text-[var(--e-text-primary)]">{'name' in moduleData ? moduleData.name : node.kind}</span>
        <span className="ml-auto rounded px-[5px] py-[1px] font-mono text-[9px] font-semibold uppercase bg-[var(--e-bg-sunken)] text-[var(--e-text-muted)]">
          {meta.categoryLabel}
        </span>
      </div>
      <div className="overflow-y-auto flex-1">
        <section className="border-b border-[var(--e-border-secondary)] px-4 py-3">
          <div className="mb-2 font-mono text-[9px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
            Data Flow
          </div>
          <div className="space-y-1">
            {meta.consumes.length > 0 && (
              <Row label="Consumes">
                <span className="font-mono text-[10px] text-[var(--e-text-primary)]">{meta.consumes.join(', ')}</span>
              </Row>
            )}
            <Row label="Produces">
              <span className="font-mono text-[10px] text-[var(--e-text-primary)]">{meta.produces.join(', ')}</span>
            </Row>
            {meta.executesAfter.length > 0 && (
              <Row label="Execution">
                <span className="font-mono text-[10px] text-[var(--e-orange-600)]">Deferred</span>
              </Row>
            )}
          </div>
        </section>
        <EnvironmentPortLegend moduleKind={node.kind} />
        <NodeAssetShelf
          moduleId={selectedModuleId}
          moduleName={'name' in moduleData ? (moduleData as any).name : node.kind}
          outputs={allModuleOutputs}
          candidates={moduleCandidates}
          exportAvailable={allModuleOutputs.length > 0}
          runId={fromRunId}
          theatreId={theatreId}
        />
        {meta.hint && (
          <section className="px-4 py-3">
            <div className="text-[10px] text-[var(--e-text-secondary)] leading-relaxed">{meta.hint}</div>
          </section>
        )}
      </div>
    </aside>
  );
}

// ── Theatre state colours ──────────────────────────────────────────────────

const THEATRE_STATE_STYLE: Record<string, { text: string; bg: string }> = {
  DRAFT: { text: 'var(--e-text-muted)', bg: 'var(--e-bg-sunken)' },
  MATERIALIZED: { text: 'oklch(0.50 0.14 250)', bg: 'oklch(0.97 0.02 250)' },
  COMMITTED: { text: 'var(--e-purple-600)', bg: 'var(--e-purple-50)' },
  ACTIVE: { text: 'var(--e-green-600)', bg: 'var(--e-green-50)' },
  SETTLING: { text: 'oklch(0.55 0.16 65)', bg: 'oklch(0.97 0.03 65)' },
  RESOLVED: { text: 'var(--e-green-600)', bg: 'var(--e-green-50)' },
  ARCHIVED: { text: 'var(--e-text-muted)', bg: 'var(--e-bg-sunken)' },
};

function stateStyle(state: string) {
  return THEATRE_STATE_STYLE[state] ?? { text: 'var(--e-text-muted)', bg: 'var(--e-bg-sunken)' };
}

function truncateHash(hash: string | null | undefined): string {
  if (!hash) return '—';
  if (hash.length <= 12) return hash;
  return `${hash.slice(0, 6)}…${hash.slice(-4)}`;
}

// ── Theatre Module Inspector (real backend data) ───────────────────────────

export function TheatreModuleInspector({
  nodeData,
  theatreId,
}: {
  nodeData: TheatreData;
  theatreId: string | null;
}) {
  const {
    theatre,
    commitment,
    certificate,
    activationReadiness,
    runtimeReadiness,
    isLoading,
  } = useTheatreDetail(theatreId ?? null);

  const hasBoundTheatre = !!theatreId && !!theatre;

  return (
    <aside className="animate-slide-in-right flex w-full min-h-0 flex-col rounded-xl border border-[var(--e-border-primary)] bg-[var(--e-bg-card)] shadow-[var(--e-shadow-sm)] overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-[var(--e-border-secondary)] px-4 py-3 shrink-0">
        <Shield className="h-4 w-4 text-[var(--e-purple-500)]" />
        <span className="text-[12px] font-semibold text-[var(--e-text-primary)]">{nodeData.name}</span>
        <span className="ml-auto font-mono text-[10px] text-[var(--e-text-muted)]">theatre_construct_draft</span>
      </div>

      <div className="overflow-y-auto flex-1">
        {/* Loading state */}
        {theatreId && isLoading && (
          <div className="flex items-center justify-center gap-2 py-6 text-[11px] text-[var(--e-text-muted)]">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading theatre…
          </div>
        )}

        {/* No theatre bound */}
        {!theatreId && (
          <section className="border-b border-[var(--e-border-secondary)] px-4 py-3">
            <div className="text-[10px] text-[var(--e-text-muted)] leading-relaxed">
              No theatre bound to this Environment. Save to a theatre to see live state.
            </div>
          </section>
        )}

        {/* Theatre state — real backend data */}
        {hasBoundTheatre && (
          <section className="border-b border-[var(--e-border-secondary)] px-4 py-3">
            <div className="mb-2 font-mono text-[9px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
              Theatre State
            </div>
            <div className="space-y-1">
              <Row label="State">
                <span
                  className="rounded px-[5px] py-[1px] font-mono text-[9px] font-semibold uppercase"
                  style={{
                    color: stateStyle(theatre.state).text,
                    backgroundColor: stateStyle(theatre.state).bg,
                  }}
                >
                  {theatre.state}
                </span>
              </Row>
              <Row label="Inquiry">
                <span className="font-mono text-[10px] text-[var(--e-text-primary)]">
                  {theatre.inquiry_class?.replace(/_/g, ' ') ?? '—'}
                </span>
              </Row>
              <Row label="Construct">
                <span className="font-mono text-[10px] text-[var(--e-text-primary)] truncate max-w-[140px] inline-block">
                  {theatre.construct_id ? truncateHash(theatre.construct_id) : '—'}
                </span>
              </Row>
            </div>
          </section>
        )}

        {/* Readiness — only for pre-ACTIVE states */}
        {hasBoundTheatre && theatre.state === 'MATERIALIZED' && activationReadiness && (
          <section className="border-b border-[var(--e-border-secondary)] px-4 py-3">
            <div className="mb-2 font-mono text-[9px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
              Activation Readiness
            </div>
            <div className="space-y-1">
              <Row label="Activatable">
                <span className={`font-mono text-[10px] font-medium ${activationReadiness.activatable ? 'text-[var(--e-green-600)]' : 'text-[oklch(0.55_0.20_25)]'}`}>
                  {activationReadiness.activatable ? 'Yes' : 'No'}
                </span>
              </Row>
              {activationReadiness.blocking_checks?.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {activationReadiness.blocking_checks.map((c: ActivationCheck, i: number) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-[oklch(0.55_0.20_25)]" />
                      <span className="text-[9px] text-[var(--e-text-secondary)] leading-[13px]">{c.detail || c.check}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {hasBoundTheatre && theatre.state === 'COMMITTED' && runtimeReadiness && (
          <section className="border-b border-[var(--e-border-secondary)] px-4 py-3">
            <div className="mb-2 font-mono text-[9px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
              Runtime Readiness
            </div>
            <div className="space-y-1">
              <Row label="Runnable">
                <span className={`font-mono text-[10px] font-medium ${runtimeReadiness.runnable ? 'text-[var(--e-green-600)]' : 'text-[oklch(0.55_0.20_25)]'}`}>
                  {runtimeReadiness.runnable ? 'Yes' : 'No'}
                </span>
              </Row>
              {runtimeReadiness.blocking_checks?.length > 0 && (
                <div className="mt-1 space-y-0.5">
                  {runtimeReadiness.blocking_checks.map((c: ActivationCheck, i: number) => (
                    <div key={i} className="flex items-start gap-1.5">
                      <span className="mt-1 h-1 w-1 shrink-0 rounded-full bg-[oklch(0.55_0.20_25)]" />
                      <span className="text-[9px] text-[var(--e-text-secondary)] leading-[13px]">{c.detail || c.check}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        )}

        {/* Proof & Certificate — only when data exists */}
        {hasBoundTheatre && (theatre.commitment_hash || certificate) && (
          <section className="border-b border-[var(--e-border-secondary)] px-4 py-3">
            <div className="mb-2 font-mono text-[9px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
              Proof
            </div>
            <div className="space-y-1">
              {theatre.commitment_hash && (
                <Row label="Commitment">
                  <span className="font-mono text-[10px] text-[var(--e-text-primary)]" title={theatre.commitment_hash}>
                    {truncateHash(theatre.commitment_hash)}
                  </span>
                </Row>
              )}
              {commitment && (
                <Row label="Committed">
                  <span className="font-mono text-[10px] text-[var(--e-text-muted)]">
                    {new Date(commitment.committed_at).toLocaleDateString()}
                  </span>
                </Row>
              )}
              {certificate && (
                <>
                  <Row label="Composite">
                    <span className="font-mono text-[10px] font-medium text-[var(--e-green-600)]">
                      {certificate.composite_score.toFixed(3)}
                    </span>
                  </Row>
                  {certificate.brier_score != null && (
                    <Row label="Brier">
                      <span className="font-mono text-[10px] font-medium text-[var(--e-text-primary)]">
                        {certificate.brier_score.toFixed(3)}
                      </span>
                    </Row>
                  )}
                  <Row label="Verification">
                    <span
                      className="rounded px-[5px] py-[1px] font-mono text-[9px] font-semibold uppercase"
                      style={{
                        color: certificate.verification_tier === 'CERTIFIED'
                          ? 'var(--e-green-600)'
                          : 'var(--e-purple-600)',
                        backgroundColor: certificate.verification_tier === 'CERTIFIED'
                          ? 'var(--e-green-50)'
                          : 'var(--e-purple-50)',
                      }}
                    >
                      {certificate.verification_tier === 'CERTIFIED' ? 'Attested' : certificate.verification_tier}
                    </span>
                  </Row>
                  <Row label="Replays">
                    <span className="font-mono text-[10px] text-[var(--e-text-primary)]">
                      {certificate.replay_count}
                    </span>
                  </Row>
                  <Row label="Deployable">
                    <span className={`font-mono text-[10px] font-medium ${certificate.is_deployable ? 'text-[var(--e-green-600)]' : 'text-[var(--e-text-muted)]'}`}>
                      {certificate.is_deployable ? 'Yes' : 'No'}
                    </span>
                  </Row>
                </>
              )}
            </div>
          </section>
        )}

        {/* Construct metadata — from canvas node data */}
        <section className="border-b border-[var(--e-border-secondary)] px-4 py-3">
          <div className="mb-2 font-mono text-[9px] font-semibold uppercase tracking-[0.06em] text-[var(--e-text-muted)]">
            Construct
          </div>
          <div className="space-y-1">
            {nodeData.construct_slug && (
              <Row label="Slug">
                <span className="font-mono text-[10px] text-[var(--e-text-primary)]">{nodeData.construct_slug}</span>
              </Row>
            )}
            {nodeData.repo && (
              <Row label="Repo">
                <span className="font-mono text-[10px] text-[var(--e-text-primary)]">{nodeData.repo}</span>
              </Row>
            )}
            <Row label="Primary">
              <span className={`font-mono text-[10px] font-medium ${nodeData.primary ? 'text-[var(--e-green-600)]' : 'text-[var(--e-text-muted)]'}`}>
                {nodeData.primary ? 'Yes' : 'No'}
              </span>
            </Row>
          </div>
        </section>

        {/* Theatre detail link */}
        {theatreId && (
          <section className="px-4 py-3">
            <Link
              to={`/theatres/${theatreId}`}
              className="inline-flex items-center gap-1.5 text-[10px] font-medium text-[var(--e-purple-600)] no-underline transition hover:text-[var(--e-purple-500)]"
            >
              <ExternalLink className="h-3 w-3" />
              View full theatre detail
            </Link>
          </section>
        )}
      </div>
    </aside>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between py-0.5">
      <span className="text-[11px] text-[var(--e-text-secondary)]">{label}</span>
      {children}
    </div>
  );
}
